import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import type {User} from 'nutshell-core/types';

import {SidebarUserAvatar} from 'shells/avatar/sidebar-user-avatar';
import {CTABanner} from 'shells/cta-banner/cta-banner';
// @ts-expect-error - imported from .js/.jsx file
import {ConnectedFormsIcon} from 'shells/icon/svg/connected-forms-icon';
import {SidebarIconLogoutIcon} from 'shells/icon/svg/sidebar-icon-logout';
// @ts-expect-error - imported from .js/.jsx file
import {SidebarIconSetupIcon} from 'shells/icon/svg/sidebar-icon-setup';

import {appHistory} from '../../history';

import {useSwitchNavigation} from '../navigation/navbar/graphql/hooks/use-switch-navigation';
import {useSidebarCollapse} from '../navigation/sidebar/use-sidebar-collapse';

import {getAgentIsOnline} from './helpers';
import {MeetYourCsm} from './meet-your-csm/meet-your-csm';
import {SidebarCompanyNameLogo} from './sidebar-company-name-logo';
import {SidebarDrawer} from './sidebar-drawer';
import {SidebarUserCompanySection} from './sidebar-user-company-section';

import styles from './sidebar-user.module.css';

type Props = {
    user: User | null | undefined;
    closeSidebar: () => void;
};

export function SidebarUser({user, closeSidebar}: Props) {
    const {switchNavigation} = useSwitchNavigation();
    const setSidebarCollapsed = useSidebarCollapse();

    const handleLinkClick = () => {
        window.scrollTo(0, 0);
        closeSidebar();
    };

    const userPermissions = user && user.permissions;

    return (
        <SidebarDrawer>
            <div className={styles.container}>
                <div className={styles['top-section']}>
                    <div>
                        <SidebarCompanyNameLogo
                            canAccessSetup={Boolean(
                                userPermissions && userPermissions.canAccessSetup
                            )}
                        />
                        {userPermissions ? (
                            <SidebarUserCompanySection
                                closeSidebar={handleLinkClick}
                                userPermissions={userPermissions}
                            />
                        ) : undefined}
                    </div>
                    <div className={styles.divider} />
                    <div className={styles['user-section']}>
                        {user ? (
                            <div className={styles['profile-info']}>
                                <SidebarUserAvatar
                                    isActive={true}
                                    className={styles.avatar}
                                    size={48}
                                    isOnline={getAgentIsOnline(user)}
                                    {...user}
                                />
                                <div className={styles.info}>
                                    <span className={styles.name}>{user.name}</span>
                                    <span className={styles.email}>{user.emails[0]}</span>
                                </div>
                            </div>
                        ) : undefined}
                        <div className={styles.menu}>
                            {/* @ts-expect-error */}
                            <RoutedLink
                                to='/my-account/settings'
                                onClick={handleLinkClick}
                                className={styles['link-container']}
                            >
                                <SidebarIconSetupIcon
                                    className={styles['link-icon-settings']}
                                    size={32}
                                />
                                <div className={styles['link-text']}>Your settings</div>
                            </RoutedLink>
                            <a href='/auth/logout' className={styles['link-container']}>
                                <SidebarIconLogoutIcon
                                    className={styles['link-icon-logout']}
                                    size={32}
                                />
                                <div className={styles['link-text']}>Log out</div>
                            </a>
                        </div>
                    </div>
                </div>
                <div>
                    {NutClientConfig.shouldShowCsmInfo ? <MeetYourCsm /> : undefined}
                    {NutClientConfig.hasNavigationFeatureFlagSwapper && (
                        <CTABanner
                            borderRadius={0}
                            fitHeight={true}
                            // @ts-expect-error
                            backgroundColor='greenLt'
                            icon={ConnectedFormsIcon}
                            title='New app navigation is coming soon!'
                            text='Try out an early preview now!'
                            buttonText='Let me see it'
                            onPrimaryAction={() => {
                                switchNavigation({isNewNavigation: true}).then(() => {
                                    appHistory.push('/');
                                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                                    user && setSidebarCollapsed(user.id, false);
                                    window.location.reload();
                                });
                                handleLinkClick();
                            }}
                        />
                    )}
                </div>
            </div>
        </SidebarDrawer>
    );
}
